import {SCondition, RequestQueryBuilder} from '@nestjsx/crud-request';
import {BaseCpnMpnInfo, BaseCpnAcnInfo, BaseCpnMsnInfo, PartHealthLogItem, BaseCPNInfo} from '../models/parts';
import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Burly} from 'kb-burly';
import {map, switchMap} from 'rxjs/operators';
import {ManyResponse} from '../../../responses/many.response';
import {Fleet} from '../models/fleet.model';
import {TorchUserService} from '../../@users';

@Injectable({
  providedIn: 'root'
})
export class TorchPartsService {

  constructor(protected config: TorchAPIConfiguration,
              private httpClient: HttpClient,
              private torchUserService: TorchUserService) {
  }

  public getFleetModelCodes(): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleets')
      .get;

    return this.httpClient.get<ManyResponse<Fleet>>(endpoint).pipe(
      map(response => response.data.map(v => v.fleetModelCode))
    );
  }

  public getATAGroups(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/cpn')
      .addSegment('/ataGroups')
      .addQuery('contains', contains)
      .get;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    return this.httpClient.get<[{ ATA_GROUP: string }]>(endpoint).pipe(
      map(response => response.map(v => v.ATA_GROUP))
    );
  }

  public getATAChapters(contains): Observable<number[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/cpn')
      .addSegment('/ataChapters')
      .addQuery('contains', contains)
      .get;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    return this.httpClient.get<[{ ATA_CHAPTER: number }]>(endpoint).pipe(
      map(response => response.map(v => v.ATA_CHAPTER))
    );
  }

  public getResourceCodes(contains): Observable<number[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/cpn')
      .addSegment('/resourceCodes')
      .addQuery('contains', (contains.length ? contains : null))
      .get;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    return this.httpClient.get<[{ REC_CD: number }]>(endpoint).pipe(
      map(response => response.map(v => v.REC_CD))
    );
  }

  public queryParts(pageSize: number = 15,
                    currentPage: number = 0,
                    sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                    sortBy: string = 'companyPartNumber',
                    filters: SCondition | null): Observable<ManyResponse<BaseCPNInfo>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }


    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/cpn')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<ManyResponse<BaseCPNInfo>>(endpoint);
  }

  public queryPartsMpn(pageSize: number = 15,
                       currentPage: number = 0,
                       sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                       sortBy: string = 'companyPartNumber',
                       filters: SCondition | null): Observable<ManyResponse<BaseCpnMpnInfo>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/cpn-mpn')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<ManyResponse<BaseCpnMpnInfo>>(endpoint);
  }

  public queryPartsMsn(pageSize: number = 15,
                       currentPage: number = 0,
                       sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                       sortBy: string = 'companyPartNumber',
                       filters: SCondition | null): Observable<ManyResponse<BaseCpnMsnInfo>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/cpn-msn')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<ManyResponse<BaseCpnMsnInfo>>(endpoint);
  }

  public queryPartsAcn(pageSize: number = 15,
                       currentPage: number = 0,
                       sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                       sortBy: string = 'companyPartNumber',
                       filters: SCondition | null): Observable<ManyResponse<BaseCpnAcnInfo>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/cpn-acn')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<ManyResponse<BaseCpnAcnInfo>>(endpoint);
  }

  public getPart(partNumber: string): Observable<BaseCPNInfo> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/cpn')
      .addSegment(`/${partNumber}`)
      .get;

    return this.httpClient.get<BaseCPNInfo>(endpoint);
  }

  public getPartHealthLog(emailAddress: string = null): Observable<PartHealthLogItem[]> {
    return this.torchUserService.getUser(emailAddress).pipe(
      switchMap(user => {
        const endpoint = Burly(this.config.rootUrl)
          .addSegment('/metrics')
          .addSegment('/recentPartHealth')
          .addSegment(`/${user.employeeId}`)
          .get;

        return this.httpClient.get<PartHealthLogItem[]>(endpoint);
      })
    );
  }

}
